import React, { useRef, useState } from 'react';
import { always, propOr } from 'ramda';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDeepCompareEffect, useMount, useMountedState } from 'react-use';

import { Artwork } from '@definitions/artworks.types';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { useMeasure } from '@hooks/useUi';
import { INCHES_TO_CM_CONVERTER } from '@modules/ui';
import { useWindowListener } from '@hooks/useWindowListener';
import { roundNumber } from '@utils/helpers';

import { THIRD_DIMS_ARTWORKS } from '../pdp.constants';

import {
  Container,
  Rectangle,
  HorizontalLine,
  VerticalLine,
  RectanglesContainer,
  FramedCopy,
  FramedCopyVertical,
} from './sketch.styles';

export interface SketchProps {
  artwork: Artwork;
}

export const Sketch = ({ artwork }: SketchProps) => {
  const isMounted = useMountedState();
  const {
    framedWidth,
    framedHeight,
    framedDepth,
    unframedHeight,
    unframedWidth,
    unframedDepth,
    subcategory,
    isFramed,
  } = artwork;
  const intl = useIntl();
  const { isInches } = useMeasure();
  const unframedRef = useRef(null);
  const framedRef = useRef(null);
  const [sketchHeight, setSketchHeight] = useState(0);
  const [framedSketchHeight, setFramedSketchHeight] = useState(0);
  const centimeterCopy = intl.formatMessage({ id: 'sketch.cm', defaultMessage: 'cm' });
  const inchesCopy = intl.formatMessage({ id: 'sketch.inches', defaultMessage: 'inches' });
  const getFormattedSize = (size: number) => (isInches ? size : size * INCHES_TO_CM_CONVERTER);
  const getSizeCopy = (size: number) =>
    `${roundNumber(getFormattedSize(size))} ${isInches ? inchesCopy : centimeterCopy}`;
  const isSculpture = subcategory === THIRD_DIMS_ARTWORKS.SCULPTURE;
  const getWidths = () => {
    const fullWidth = 86;

    if (!unframedDepth) {
      return { rectangleWidth: fullWidth, rectangleDepth: 0 };
    }
    if (unframedWidth === unframedDepth) {
      return { rectangleWidth: fullWidth / 2, rectangleDepth: fullWidth / 2 };
    }

    const widthPercent = (100 * unframedWidth) / (unframedWidth + unframedDepth);

    return {
      rectangleWidth: (fullWidth * widthPercent) / 100,
      rectangleDepth: (fullWidth * (100 - widthPercent)) / 100,
    };
  };
  const { rectangleWidth, rectangleDepth } = getWidths();

  const setNewHeight = () => {
    const { offsetWidth: unframedRectangleWidth } = propOr({ offsetWidth: 0 }, 'current', unframedRef);
    const { offsetWidth: framedRectangleWidth } = propOr({ offsetWidth: 0 }, 'current', framedRef);

    setSketchHeight(Math.round((unframedRectangleWidth * unframedHeight) / unframedWidth));
    if (isFramed) {
      setFramedSketchHeight(Math.round((framedRectangleWidth * framedHeight) / framedWidth));
    }
  };

  useWindowListener('resize', setNewHeight, { throttle: 200 });

  useDeepCompareEffect(() => {
    if (isMounted()) {
      setNewHeight();
    }
  }, [artwork]);

  useMount(() => {
    setNewHeight();
  });

  const renderDepthSketch = renderWhenTrue(
    always(
      <Rectangle width={unframedDepth} dashed={isSculpture} height={sketchHeight}>
        <HorizontalLine>{getSizeCopy(unframedDepth)}</HorizontalLine>
        <VerticalLine>{getSizeCopy(unframedHeight)}</VerticalLine>
      </Rectangle>
    )
  );

  const renderFramedDepthSketchMessage = renderWhenTrue(
    always(
      <FramedCopyVertical>
        <FormattedMessage id="sketch.withFrame" defaultMessage="with frame" />
      </FramedCopyVertical>
    )
  );

  const renderFramedDepthSketch = renderWhenTrue(
    always(
      <Rectangle width={framedDepth} dashed={isSculpture} height={framedSketchHeight}>
        {renderFramedDepthSketchMessage(framedDepth >= 10)}
        <HorizontalLine>{getSizeCopy(framedDepth)}</HorizontalLine>
        <VerticalLine>{getSizeCopy(framedHeight)}</VerticalLine>
      </Rectangle>
    )
  );

  const renderFramedSketch = renderWhenTrue(
    always(
      <RectanglesContainer>
        <Rectangle withFrame height={framedSketchHeight} width={rectangleWidth} ref={framedRef}>
          <FramedCopy>
            <FormattedMessage id="sketch.withFrame" defaultMessage="with frame" />
          </FramedCopy>
          <HorizontalLine>{getSizeCopy(framedWidth)}</HorizontalLine>
          <VerticalLine>{getSizeCopy(framedHeight)}</VerticalLine>
        </Rectangle>
        {renderFramedDepthSketch(!!framedDepth)}
      </RectanglesContainer>
    )
  );

  const renderSketch = renderWhenTrueOtherwise(
    always(
      <RectanglesContainer>
        <Rectangle ref={unframedRef} dashed width={rectangleWidth} height={sketchHeight}>
          <HorizontalLine>{getSizeCopy(unframedWidth)}</HorizontalLine>
          <VerticalLine>{getSizeCopy(unframedHeight)}</VerticalLine>
        </Rectangle>
        {renderDepthSketch(!!unframedDepth)}
      </RectanglesContainer>
    ),
    always(
      <RectanglesContainer>
        <Rectangle ref={unframedRef} width={rectangleWidth} height={sketchHeight}>
          <HorizontalLine>{getSizeCopy(unframedWidth)}</HorizontalLine>
          <VerticalLine>{getSizeCopy(unframedHeight)}</VerticalLine>
        </Rectangle>
        {renderDepthSketch(!!unframedDepth)}
      </RectanglesContainer>
    )
  );

  return (
    <Container>
      {renderSketch(isSculpture && !!sketchHeight)}
      {renderFramedSketch(isFramed && !isSculpture)}
    </Container>
  );
};
